import React, { Fragment, type ReactNode, useEffect, useRef } from 'react';

import { useAnalytics } from '../analytics/use-analytics';
import { useEditionAwareness } from '../services/edition-awareness-context';
import { useGetExperimentValue } from '../services/experiment-context';
import { isEligibleForJWMExperiment } from '../services/is-eligible-for-jwm-experiment';

import { ActionsAndContent } from './actions-and-content';

/**
 * One off event which tracks when a customer is eligible/ineligible for edition awareness
 */
function useTrackAudienceAndTiming(isEligible: boolean) {
	const { sendAnalyticsEvent } = useAnalytics();
	const hasSent = useRef(false);

	useEffect(() => {
		if (hasSent.current) {
			return;
		}

		sendAnalyticsEvent({
			eventType: 'operational',
			actionSubject: 'audienceAndTiming',
			actionSubjectId: 'editionAwareness',
			action: 'checked',
			attributes: {
				isEligible,
			},
		});

		hasSent.current = true;
	}, [sendAnalyticsEvent, isEligible]);
}

/**
 * Tracks when a user should see the edition awareness experience
 */
function Analytics({ children }: { children: ReactNode }) {
	useTrackAudienceAndTiming(true);

	return <Fragment>{children}</Fragment>;
}

/**
 * Just sends ineligibility info through a `eligibilityCriteria checked` event.
 * This exists so we can optionally render this component
 */
export function IneligibleAnalytics() {
	useTrackAudienceAndTiming(false);

	return null;
}

/**
 * Based on the audience and timing (e.g. 3 days before the trial ends) we decide to show
 * the free/trial button to the customer. If we want to change the logic of if we show the button or
 * not, it should happen here.
 */
export function AudienceAndTiming(): React.JSX.Element | null {
	const context = useEditionAwareness();
	const { isEnterpriseCustomer } = context;

	const { getExperimentValue } = useGetExperimentValue();

	// EA should not be shown to Enterprise customers as per: https://atlassiansupport.atlassian.net/browse/CES-106245
	const shouldSeeButton = !isEnterpriseCustomer;

	if (!shouldSeeButton) {
		return <IneligibleAnalytics />;
	}

	if (shouldSeeButton) {
		if (
			isEligibleForJWMExperiment(context) &&
			getExperimentValue({
				statsigParams: {
					experimentName: 'twcg-1563-remove-jira-only-restriction',
					parameterName: 'cohort',
					defaultValue: 'not-enrolled',
				},
			}) !== 'variation'
		) {
			// If the tenant is not in variation, we don't show anything, as that is the original JWM experience
			return <IneligibleAnalytics />;
		}
	}

	return (
		<Analytics>
			<ActionsAndContent />
		</Analytics>
	);
}
